/**
 * @license
 * Copyright 2018-2022 Streamlit Inc.
 * Copyright 2022 Yuichiro Tachibana
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *    http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { SessionInfo } from "streamlit-browser/src/lib/SessionInfo";
import _ from "lodash";
import { FormDataEncoder } from "form-data-encoder";
import { isValidFormId } from "streamlit-browser/src/lib/utils";
/**
 * Handles uploading files to the server.
 */
export class FileUploadClient {
    constructor(props) {
        /**
         * Map of <formId: number of outstanding requests>. Updated whenever
         * a widget in a form creates are completes a request.
         */
        this.formsWithPendingRequests = new Map();
        this.pendingFormUploadsChanged = props.formsWithPendingRequestsChanged;
    }
    setKernel(kernel) {
        this.kernel = kernel;
    }
    /**
     * Upload a file to the server. It will be associated with this browser's sessionID.
     *
     * @param widget: the FileUploader widget that's doing the upload.
     * @param file: the files to upload.
     * @param onUploadProgress: an optional function that will be called repeatedly with progress events during the upload.
     * @param cancelToken: an optional axios CancelToken that can be used to cancel the in-progress upload.
     *
     * @return a Promise<number> that resolves with the file's unique ID, as assigned by the server.
     */
    uploadFile(widget, file, onUploadProgress, // TODO
    cancelToken // TODO
    ) {
        return __awaiter(this, void 0, void 0, function* () {
            const form = new FormData();
            form.append("sessionId", SessionInfo.current.sessionId);
            form.append("widgetId", widget.id);
            form.append(file.name, file);
            this.offsetPendingRequestCount(widget.formId, 1);
            const encoder = new FormDataEncoder(form);
            const bodyBlob = new Blob(encoder, {
                type: encoder.contentType,
            });
            return bodyBlob.arrayBuffer().then((body) => {
                if (this.kernel == null) {
                    throw new Error("Kernel not ready");
                }
                return this.kernel
                    .sendHttpRequest({
                    method: "POST",
                    path: "/upload_file",
                    body,
                    headers: Object.assign({}, encoder.headers),
                })
                    .then((response) => {
                    const text = new TextDecoder().decode(response.body);
                    if (typeof text === "number") {
                        return text;
                    }
                    if (typeof text === "string") {
                        const parsed = parseInt(text, 10);
                        if (!Number.isNaN(parsed)) {
                            return parsed;
                        }
                    }
                    throw new Error(`Bad uploadFile response: expected a number but got '${text}'`);
                })
                    .finally(() => this.offsetPendingRequestCount(widget.formId, -1));
            });
        });
    }
    getFormIdSet() {
        return new Set(this.formsWithPendingRequests.keys());
    }
    offsetPendingRequestCount(formId, offset) {
        var _a;
        if (offset === 0) {
            return;
        }
        if (!isValidFormId(formId)) {
            return;
        }
        const curCount = (_a = this.formsWithPendingRequests.get(formId)) !== null && _a !== void 0 ? _a : 0;
        const newCount = curCount + offset;
        if (newCount < 0) {
            throw new Error(`Can't offset pendingRequestCount below 0 (formId=${formId}, curCount=${curCount}, offset=${offset})`);
        }
        const prevWidgetIds = this.getFormIdSet();
        if (newCount === 0) {
            this.formsWithPendingRequests.delete(formId);
        }
        else {
            this.formsWithPendingRequests.set(formId, newCount);
        }
        const newWidgetIds = this.getFormIdSet();
        if (!_.isEqual(newWidgetIds, prevWidgetIds)) {
            this.pendingFormUploadsChanged(newWidgetIds);
        }
    }
}
//# sourceMappingURL=FileUploadClient.js.map